const DARK_QUERY = '(prefers-color-scheme: dark)'
const ICON_SELECTOR = 'link[rel~="icon"]'

/**
 * Point the tab icon at a partner's favicon and keep its light/dark pair in
 * step with the OS/browser colour scheme. Returns a cleanup that puts the
 * original icons back. See components/ThemeFavicon.tsx for why this exists.
 *
 * Every icon link is rewritten IN PLACE, never removed and replaced. Next.js
 * owns the `<link rel="icon" href="/favicon.ico">` it rendered, and re-inserts
 * it when it reconciles the head; a link we'd added beside it then loses,
 * because the browser uses the last icon in the document. So we edit whatever
 * icon links exist, and a MutationObserver edits any Next adds later.
 */
export function mountThemeFavicon(
  light: string,
  dark: string | null | undefined,
  doc: Document = document,
  win: Window & typeof globalThis = window,
): () => void {
  const mq = dark ? win.matchMedia(DARK_QUERY) : null
  const target = () => (mq?.matches && dark ? dark : light)
  const originals = new Map<HTMLLinkElement, { href: string | null; media: string | null }>()
  let added: HTMLLinkElement | null = null

  const point = (link: HTMLLinkElement) => {
    if (!originals.has(link)) {
      originals.set(link, { href: link.getAttribute('href'), media: link.getAttribute('media') })
    }
    const href = target()
    // A `media` left on a link would let the browser skip it in one scheme.
    if (link.hasAttribute('media')) link.removeAttribute('media')
    if (link.getAttribute('href') !== href) link.setAttribute('href', href)
  }

  const apply = () => {
    const links = Array.from(doc.querySelectorAll<HTMLLinkElement>(ICON_SELECTOR))
    if (links.length === 0) {
      added = doc.createElement('link')
      added.rel = 'icon'
      doc.head.appendChild(added)
      links.push(added)
    }
    links.forEach(point)
  }
  apply()
  mq?.addEventListener('change', apply)

  // Our own setAttribute calls fire the observer too; `point` is a no-op for a
  // link that already has the right href, so this settles instead of looping.
  const observer = new win.MutationObserver(() => apply())
  observer.observe(doc.head, { childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'rel', 'media'] })

  return () => {
    observer.disconnect()
    mq?.removeEventListener('change', apply)
    added?.remove()
    originals.forEach((o, link) => {
      if (link === added) return
      if (o.href === null) link.removeAttribute('href')
      else link.setAttribute('href', o.href)
      if (o.media === null) link.removeAttribute('media')
      else link.setAttribute('media', o.media)
    })
  }
}
